import * as _4 from 'lodash4';
import { FastConditionalStrategy } from 'utility-ts-strategy';
import { APTerm } from '../../global/builders/ap_term_builder';
import { APChangelog, IAPSuggestedPlanItem, proposer } from '../builders/ap_changelog_builder';
import {
  ChangeType,
  ChangeTypeStrategy,
  ChangeTypeTemplateStrategy,
  ICommentDetails,
  PROPOSER,
  SuggestionTypeStrategy
} from '../strategies/ap_changelog_strategy';

export class APChangelogServant {
  static metAssociatedTransactions = ['MET', 'UNMET', 'COMMENT_ONLY'];
  // Interview Question?
  static createSentenceForNonMetSuggestion(suggestedChanges: IAPSuggestedPlanItem[], changeType: ChangeType) {
    const courseChangeWords = ChangeTypeTemplateStrategy.getByKey(changeType).courseTemplate(suggestedChanges);
    const commentDetails = ChangeTypeTemplateStrategy.getByKey(changeType).commentTemplate(suggestedChanges);
    const lockedText = ChangeTypeTemplateStrategy.getByKey(changeType).lockTemplate(suggestedChanges);
    const flagLockwords: any = [];

    if (!_4.isEmpty(lockedText)) {
      flagLockwords.push(`${lockedText}`);
    }

    if (commentDetails && !_4.isEmpty(commentDetails.comment)) {
      const flagText = commentDetails.hasFlag ? `added a flag` : `removed flag`;
      flagLockwords.push(`${flagText}`);
    }
    const sentenceWords = _4.compact(courseChangeWords.concat(flagLockwords));
    const middleWords = _4.slice(sentenceWords, 1, -1);
    const middleSentense = _4.isEmpty(middleWords) ? `` : `${middleWords.join(', ')} and `;
    const displaySentence = `${_4.head(sentenceWords)} ${middleSentense}${_4.last(sentenceWords)}`;
    return {
      displaySentence,
      commentDetails
    };
  }
  static getSlotTitle(suggestedChange: IAPSuggestedPlanItem) {
    const fallbackTitle = _4.get(suggestedChange, 'course_group.nk', '');
    const programSlotDesc = _4.get(suggestedChange, 'program_slot.desc', '');
    return programSlotDesc || fallbackTitle;
  }

  static createSentenceForMetSuggestion(suggestedChanges: IAPSuggestedPlanItem[], changeType: ChangeType) {
    if (!_4.includes(APChangelogServant.metAssociatedTransactions, changeType)) {
      return {
        displaySentence: ``,
        commentDetails: {} as ICommentDetails
      };
    }
    const suggestedChange = changeType !== 'COMMENT_ONLY' ? _4.first(suggestedChanges) : _4.last(suggestedChanges);
    const slotTitle =
      suggestedChange.slot_type === 'COURSE'
        ? `${suggestedChange.course.cd} ${suggestedChange.course.title}`
        : APChangelogServant.getSlotTitle(suggestedChange);
    if (changeType === 'MET') {
      return {
        displaySentence: `${slotTitle} marked as met`,
        commentDetails: {
          comment: suggestedChange.comment,
          prefix: 'added',
          hasFlag: false
        }
      };
    } else if (changeType === 'COMMENT_ONLY') {
      return {
        displaySentence: `${slotTitle}'s mark as met comment got updated`,
        commentDetails: {
          comment: suggestedChange.comment,
          prefix: 'updated',
          hasFlag: false
        }
      };
    } else {
      return {
        displaySentence: `${slotTitle} marked as unmet`,
        commentDetails: {
          comment: suggestedChange.comment,
          prefix: 'deleted',
          hasFlag: false
        }
      };
    }
  }

  static createChangeSentence(suggestedChanges: IAPSuggestedPlanItem[]) {
    const sentenceFormStrategy = new FastConditionalStrategy([
      [
        APChangelogServant.createSentenceForMetSuggestion,
        (_changeType: ChangeType) => _4.includes(APChangelogServant.metAssociatedTransactions, _changeType)
      ],
      [
        APChangelogServant.createSentenceForNonMetSuggestion,
        (_changeType: ChangeType) => !_4.includes(APChangelogServant.metAssociatedTransactions, _changeType)
      ]
    ]);

    const changeType = ChangeTypeStrategy.evaluate(suggestedChanges);
    return sentenceFormStrategy.evaluate(changeType, APChangelogServant.createSentenceForNonMetSuggestion)(
      suggestedChanges,
      changeType
    );
  }

  static getProposerChange(suggestedChanges: IAPSuggestedPlanItem[], changeProposer: proposer) {
    return _4.find(suggestedChanges, { proposer: changeProposer });
  }
  static getProposerChangeProperty(
    suggestedChanges: IAPSuggestedPlanItem[],
    changeProposer: proposer,
    prop: keyof IAPSuggestedPlanItem
  ): APTerm {
    return _4.get(this.getProposerChange(suggestedChanges, changeProposer), prop);
  }

  static hasTermChanged(suggestedChanges: IAPSuggestedPlanItem[]) {
    return this.hasPropertyChanged(`term`, suggestedChanges);
  }

  static hasPropertyChanged(prop: keyof IAPSuggestedPlanItem, suggestedChanges: IAPSuggestedPlanItem[]) {
    const advisorChangeProp: IAPSuggestedPlanItem[keyof IAPSuggestedPlanItem] = this.getProposerChangeProperty(
      suggestedChanges,
      PROPOSER.advisor,
      prop
    );
    const studentChangeProp = this.getProposerChangeProperty(suggestedChanges, PROPOSER.student, prop);
    return !_4.isEqual(advisorChangeProp, studentChangeProp);
  }

  static hasComment(change: Pick<IAPSuggestedPlanItem, 'comment'>) {
    return !_4.isEmpty(change.comment);
  }

  static isCourse(course: Pick<IAPSuggestedPlanItem, 'course'>) {
    return !_4.isNull(course.course);
  }

  static getChangelogDisplayText(changelogItem: APChangelog) {
    const isStatusResolved = _4.isEqual(_4.get(changelogItem, 'status', ''), 'RESOLVED');
    _4.each(changelogItem.suggested_plan_resolutions, (resolutionItem) => {
      const suggestionChanges = this.createChangeSentence(resolutionItem.suggested_plan_changes);
      const isConflictingResolution = isStatusResolved && _4.isEqual(resolutionItem.resolution, 'UNDECIDED');
      const displayObject = {
        ...SuggestionTypeStrategy[isConflictingResolution ? 'CONFLICTING' : resolutionItem.resolution],
        items: [suggestionChanges]
      };
      const existingChangelogType = _4.find(changelogItem._changelogDisplay, { type: displayObject.type });
      if (existingChangelogType) {
        existingChangelogType.items = _4.concat(existingChangelogType.items, displayObject.items);
      } else {
        changelogItem._changelogDisplay.push(displayObject);
      }
    });

    return changelogItem;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/changelog/servants/ap_changelog_servant.ts